import type { Vertex, Edge, HydraulicSystem } from '@shared/hydraulic'

export function nextVertexId(existingVertices: Vertex[], claimedIds?: Set<string>): string {
  let maxNum = 0
  for (const v of existingVertices) {
    const match = v.id.match(/^J(\d+)$/)
    if (match) maxNum = Math.max(maxNum, parseInt(match[1]!, 10))
  }
  if (claimedIds) {
    for (const id of claimedIds) {
      const match = id.match(/^J(\d+)$/)
      if (match) maxNum = Math.max(maxNum, parseInt(match[1]!, 10))
    }
  }
  let candidate = `J${maxNum + 1}`
  const existingIds = new Set(existingVertices.map(v => v.id))
  while (existingIds.has(candidate) || claimedIds?.has(candidate)) {
    maxNum++
    candidate = `J${maxNum + 1}`
  }
  return candidate
}

export function nextEdgeId(existingEdges: Edge[], claimedIds?: Set<string>): string {
  let maxNum = 0
  for (const e of existingEdges) {
    const match = e.id.match(/^P(\d+)$/)
    if (match) maxNum = Math.max(maxNum, parseInt(match[1]!, 10))
  }
  if (claimedIds) {
    for (const id of claimedIds) {
      const match = id.match(/^P(\d+)$/)
      if (match) maxNum = Math.max(maxNum, parseInt(match[1]!, 10))
    }
  }
  let candidate = `P${maxNum + 1}`
  const existingIds = new Set(existingEdges.map(e => e.id))
  while (existingIds.has(candidate) || claimedIds?.has(candidate)) {
    maxNum++
    candidate = `P${maxNum + 1}`
  }
  return candidate
}

/**
 * Generate a globally-unique scenario node id. Unlike vertex/edge ids,
 * scenario ids are not sequence-numbered (no stable display ordering to
 * preserve), so a UUID is the simplest collision-free choice.
 */
export function nextScenarioId(): string {
  return `scn-${crypto.randomUUID()}`
}

// Legacy aliases (backward compatible — claimedIds is optional)
export const nextDeviceId = nextVertexId as (items: { id: string }[], claimedIds?: Set<string>) => string
export const nextPipeId = nextEdgeId as (items: { id: string }[], claimedIds?: Set<string>) => string

/**
 * Generate the next available Results Schedule ID in the RS-001, RS-002, ...
 * sequence. Reads existing IDs from system.diagram_elements. Returns 'RS-001'
 * when system is null or diagram_elements is empty.
 */
export function nextScheduleId(system: HydraulicSystem | null): string {
  const elements = system?.diagram_elements ?? []
  let maxNum = 0
  for (const el of elements) {
    const match = el.id.match(/^RS-(\d+)$/)
    if (match) maxNum = Math.max(maxNum, parseInt(match[1]!, 10))
  }
  const existingIds = new Set(elements.map(el => el.id))
  let num = maxNum + 1
  let candidate = `RS-${String(num).padStart(3, '0')}`
  while (existingIds.has(candidate)) {
    num++
    candidate = `RS-${String(num).padStart(3, '0')}`
  }
  return candidate
}
